/**
 * Formato de valores. Un solo lugar, para que la misma clase de dato se lea
 * igual en toda la consola: una hora tiene que verse igual en la cola de
 * documentos que en la auditoria, o comparar dos pantallas cuesta el doble.
 *
 * Zona horaria fija en Lima. La consola opera en Peru y quien la lee compara
 * contra la hora del reloj de la pared, no contra la de su navegador.
 */
const ZONA = 'America/Lima';

const horaCorta = new Intl.DateTimeFormat('es-PE', {
  timeZone: ZONA,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const fechaHora = new Intl.DateTimeFormat('es-PE', {
  timeZone: ZONA,
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const fechaSola = new Intl.DateTimeFormat('es-PE', {
  timeZone: ZONA,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

export const laHora = (iso: string | null): string => (iso ? horaCorta.format(new Date(iso)) : '—');

export const laFechaHora = (iso: string | null): string =>
  iso ? fechaHora.format(new Date(iso)) : '—';

export const laFecha = (iso: string | null): string =>
  iso ? fechaSola.format(new Date(iso)) : '—';

/** Segundos a `m:ss`, que es como se lee un ETA en voz alta. */
export const duracion = (segundos: number | null): string => {
  if (segundos === null) return '—';
  const m = Math.floor(segundos / 60);
  const s = Math.abs(segundos % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

export const metros = (m: number | null): string =>
  m === null ? '—' : m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${m} m`;

/** `moneda` es un código ISO-4217, como lo declara `TarifaReferencial` del contrato. */
export const dinero = (monto: number, moneda: string): string =>
  new Intl.NumberFormat('es-PE', {
    style: 'currency',
    currency: moneda,
    minimumFractionDigits: 2,
  }).format(monto);

export const porcentaje = (fraccion: number | null): string =>
  fraccion === null ? '—' : `${(fraccion * 100).toFixed(1)} %`;

/** Cuanto hace, en texto corto. Para "hace 4 min" en una lista de incidentes. */
export const haceCuanto = (iso: string | null, ahora: Date): string => {
  if (!iso) return '—';
  const min = Math.round((ahora.getTime() - new Date(iso).getTime()) / 60_000);
  if (min < 1) return 'recién';
  if (min < 60) return `hace ${min} min`;
  const horas = Math.round(min / 60);
  if (horas < 48) return `hace ${horas} h`;
  return `hace ${Math.round(horas / 24)} d`;
};

/** Días que faltan para una fecha. Negativo = ya vencio. */
export const diasHasta = (iso: string | null, ahora: Date): number | null =>
  iso === null ? null : Math.round((new Date(iso).getTime() - ahora.getTime()) / 86_400_000);
